import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query";
import {DateRange} from "react-day-picker";

import * as apiClient from "@/lib/api";
import {formatDate} from "@/lib/filters/date/date-utils";
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset";
import {
  NpsRating,
  OverallRating, QualityOfAdviceRating, QualityOfServiceRating,
  ReviewInvitationStats,
  ReviewsNumber, ValueForMoneyRating
} from "@/modules/elevation-verified/types";
import {useDate} from "@/stores/date-filter";
import {
  QUERY_KEY_NPS_RATING,
  QUERY_KEY_OVERALL_RATING,
  QUERY_KEY_QUALITY_OF_ADVICE_RATING,
  QUERY_KEY_QUALITY_OF_SERVICE_RATING, QUERY_KEY_RATINGS,
  QUERY_KEY_REVIEW_INVITATION_STATS,
  QUERY_KEY_REVIEWS_NUMBER, QUERY_KEY_VALUE_FOR_MONEY_RATING
} from "@/utils/keys";

function fetchReviewInvitationStats(date: DateRange): Promise<ReviewInvitationStats> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/activity-levels/review-invitations?&to=${to}&from=${from}`)
}

export const reviewInvitationStatsQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_REVIEW_INVITATION_STATS, date],
  queryFn: () => fetchReviewInvitationStats(date),
  placeholderData: keepPreviousData
})

export const useReviewInvitationStats = () => {
  const { date } = useDate()

  const queryOptions = reviewInvitationStatsQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchReviewsNumber(date: DateRange): Promise<ReviewsNumber> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/activity-levels/reviews?&to=${to}&from=${from}`)
}
export const reviewsNumberQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_REVIEWS_NUMBER, date],
  queryFn: () => fetchReviewsNumber(date),
  placeholderData: keepPreviousData
})

export const useReviewsNumber = () => {
  const { date } = useDate()

  const queryOptions = reviewsNumberQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchOverallRating(date: DateRange): Promise<OverallRating> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/overall-rating?&to=${to}&from=${from}`)
}

export const overallRatingQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_RATINGS, QUERY_KEY_OVERALL_RATING, date],
  queryFn: () => fetchOverallRating(date),
  placeholderData: keepPreviousData
})

export const useOverallRating = () => {
  const { date } = useDate()

  const queryOptions = overallRatingQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchQualityOfAdviceRating(date: DateRange): Promise<QualityOfAdviceRating> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/quality-of-advice?&to=${to}&from=${from}`)
}

export const qualityOfAdviceRatingQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_RATINGS, QUERY_KEY_QUALITY_OF_ADVICE_RATING, date],
  queryFn: () => fetchQualityOfAdviceRating(date),
  placeholderData: keepPreviousData
})

export const useQualityOfAdviceRating = () => {
  const { date } = useDate()

  const queryOptions = qualityOfAdviceRatingQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchQualityOfServiceRating(date: DateRange): Promise<QualityOfServiceRating> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/quality-of-service?&to=${to}&from=${from}`)
}

export const qualityOfServiceRatingQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_RATINGS, QUERY_KEY_QUALITY_OF_SERVICE_RATING, date],
  queryFn: () => fetchQualityOfServiceRating(date),
  placeholderData: keepPreviousData
})

export const useQualityOfServiceRating = () => {
  const { date } = useDate()

  const queryOptions = qualityOfServiceRatingQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchValueForMoneyRating(date: DateRange): Promise<ValueForMoneyRating> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/value-for-money?&to=${to}&from=${from}`)
}

export const valueForMoneyRatingQueryOptions = (date: DateRange) => queryOptions({
  queryKey: [QUERY_KEY_RATINGS, QUERY_KEY_VALUE_FOR_MONEY_RATING, date],
  queryFn: () => fetchValueForMoneyRating(date),
  placeholderData: keepPreviousData
})

export const useValueForMoneyServiceRating = () => {
  const { date } = useDate()
  const queryOptions = valueForMoneyRatingQueryOptions(date)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

function fetchNpsRating(date: DateRange): Promise<NpsRating> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/dashboard/nps-score?&to=${to}&from=${from}`)
}

export const npsRatingQueryOptions = (date: DateRange, enabled: boolean) => queryOptions({
  queryKey: [QUERY_KEY_RATINGS, QUERY_KEY_NPS_RATING, date],
  queryFn: () => fetchNpsRating(date),
  placeholderData: keepPreviousData,
  enabled,
})

export const useNpsRating = (enabled: boolean) => {
  const { date } = useDate()
  const queryOptions = npsRatingQueryOptions(date, enabled)
  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}
